import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { formatMoney } from '../utils/formatters';
import { getAdTitle, getAdDescription } from '../utils/adI18n';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { formatExactDate } from '../utils/timeAgo';
import { formatMemberSince, formatRelativePostedTime, getSellerActiveAdCount } from '../utils/dataConsistency';
import { AdShare } from '../components/AdShare';
import { AdImageGallery } from '../components/AdImageGallery';
import BoostSelectionModal from '../components/BoostSelectionModal';
import { COINS_DISABLED } from '../config/coinsFlag';
import ReportAdSheet from '../components/ReportAdSheet';
import AdActionsMenu from '../components/AdActionsMenu';
import RelatedAdsCarousel from '../components/RelatedAdsCarousel';
import InlineError from '../components/InlineError';
import AdViewsBadge from '../components/AdViewsBadge';
import { buildShareUrl, shareNative, copyText } from '../utils/shareUtils';
import AppHeader from '../components/AppHeader';
import SafetyTipsCard from '../components/SafetyTipsCard';
import { AvailabilityBadge } from '../components/AvailabilityBadge';
import { getLocalizedLocationLabel } from '../components/AdInfoCard';
import { guardImage, getCategoryPlaceholder } from '../lib/imageTrust';
import { normalizeAdImages } from '../lib/adImages';
import { toAbsoluteUrl } from '../lib/media';
import BoostTimerBadge from '../components/BoostTimerBadge';
import { PremiumVerifiedBadge } from '../components/PremiumVerifiedBadge';
import { getAdCategoryLabel } from '../i18n/categories';
import { MapPin, Tag, Zap, AlertTriangle, AlertCircle, Search, Clock3, Clock, Eye, UserCircle2, Bookmark, Heart, ShieldCheck, Users, Package, BarChart3, Check, Star, Truck } from 'lucide-react';
import { getSellerTrustLabel, getSellerBadge } from '../utils/sellerTrust';
import { trackListingView, trackContactClick, trackWhatsAppClick, trackPhoneClick } from '../utils/analytics';
import { addRecentlyViewed } from '../utils/recentlyViewed';
import { hideListing, isListingHidden } from '../utils/hiddenListings';
import { computeProfileStrength } from '../utils/profileStrength';
import { normalizeInternationalPhone, toWhatsAppLink } from '../utils/phone';
import { BuyerProtection, TrustSection, BoostedBadge, isSellerVerified } from '../components/TrustBadges';
import { SellerRatingBadge, SellerReviewsSheet, LeaveReviewModal, useSellerReviewEligibility } from '../components/SellerReviews';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import './AdDetails.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// iter310 — legacy helper kept only for the rare non-Sudan number; for
// Sudan numbers we rely on `normalizeSudanPhone` so the WhatsApp link
// always includes the +249 country code (was the root cause of the
// "Couldn't look up phone number" error WhatsApp surfaced when the
// stored value was a bare local "09…").
function onlyDigitsPhone(v = "") {
  return String(v).replace(/[^\d+]/g, '').replace(/^\+/, '');
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.47 14.38c-.27-.14-1.6-.79-1.84-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.05-.16.18-.31.2-.58.07-.27-.14-1.12-.41-2.14-1.3-.79-.71-1.33-1.58-1.48-1.85-.16-.27-.02-.41.12-.55.12-.12.27-.31.41-.47.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.48-.84-2.03-.22-.52-.45-.45-.61-.46-.16-.01-.34-.01-.52-.01-.18 0-.48.07-.73.34-.25.27-.96.94-.96 2.29s.98 2.65 1.11 2.84c.14.18 1.93 2.95 4.68 4.13.65.28 1.16.45 1.56.57.65.21 1.24.18 1.71.11.52-.08 1.6-.65 1.82-1.27.23-.61.23-1.14.16-1.25-.06-.11-.24-.18-.52-.31z" />
      <path d="M12 2a10 10 0 0 0-8.64 15.03L2 22l5.1-1.34A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.03.8.8-2.95-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
    </svg>
  );
}

// Hook to register ad view with deduplication
function useRegisterAdView(adId, initialCount, setViewCount) {
  const sentRef = useRef(false);

  useEffect(() => {
    if (!adId) return;
    if (sentRef.current) return;
    sentRef.current = true;

    const controller = new AbortController();

    (async () => {
      try {
        if (typeof initialCount === "number") setViewCount(initialCount);

        const token = localStorage.getItem("token");
        const headers = { "Content-Type": "application/json" };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/api/ads/${adId}/view`, {
          method: "POST",
          headers,
          signal: controller.signal,
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data?.viewCount === "number") setViewCount(data.viewCount);
      } catch (e) {
        // Silently ignore errors
      }
    })();

    return () => controller.abort();
  }, [adId, initialCount, setViewCount]);
}

function AdDetails() {
  const { t, i18n } = useTranslation();
  const { lang } = useLanguage();
  const { user, updateUser } = useAuth();
  const isArabic = i18n.language?.startsWith('ar');
  const { adId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sellerAvatarFailed, setSellerAvatarFailed] = useState(false);
  useEffect(() => { setSellerAvatarFailed(false); }, [adId]);
  const [error, setError] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const [viewCount, setViewCount] = useState(0);
  const [showReportSheet, setShowReportSheet] = useState(false);
  const [showPostedDetails, setShowPostedDetails] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [priceWarning, setPriceWarning] = useState(false);
  const [priceCache, setPriceCache] = useState(null);
  // MVP: hide the public-facing Price Insight card. Backend/DB untouched;
  // dormant fetch + JSX kept behind this flag so re-enabling = flip to true.
  const SHOW_PRICE_INSIGHT = false;
  const [sellerAds, setSellerAds] = useState([]);
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  // iter331 — Owner-only "Delete ad" confirmation, triggered from the
  // three-dot ⋮ menu on the hero. Defaults closed; not shown to
  // non-owners (the menu hides the Delete item for them).
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // iter317 — Seller reviews & ratings (gated by db.contact_logs).
  // Two surfaces: SellerReviewsSheet (read) and LeaveReviewModal (write).
  const [showReviewsSheet, setShowReviewsSheet] = useState(false);
  const [showLeaveReviewModal, setShowLeaveReviewModal] = useState(false);
  
  // Memoized date formatting for performance
  const locale = isArabic ? 'ar' : 'en';
  const timeAgo = React.useMemo(() => formatRelativePostedTime(ad?.created_at, locale), [ad?.created_at, locale]);
  const exactPosted = React.useMemo(() => formatExactDate(ad?.created_at, locale), [ad?.created_at, locale]);
  const exactUpdated = React.useMemo(
    () => (ad?.updated_at ? formatExactDate(ad.updated_at, locale) : ''),
    [ad?.updated_at, locale]
  );
  
  // Check if current user is the owner of this ad
  // Use String() comparison to handle type mismatches
  const isOwner = user && ad && String(user.user_id || '') === String(ad.user_id || '');

  // iter349 — Prefetch review eligibility so the "Rate seller" CTA
  // and the seller-reviews-sheet reflect the *known* state before
  // any modal opens. If the buyer has no contact_log with this
  // seller yet, the CTA label + tap behavior surfaces the warning
  // inline instead of opening a modal that would just flash and
  // dead-end. Gated by !isOwner because the owner can never review
  // their own ad.
  const sellerUserId = ad?.user_id || ad?.seller?.user_id || null;
  const reviewEligibilityEnabled = !!(user && sellerUserId && !isOwner);
  const { eligibility: reviewEligibility, refresh: refreshReviewEligibility } =
    useSellerReviewEligibility(sellerUserId, reviewEligibilityEnabled);

  // iter331 — Three-dot menu action handlers. These wrap the existing
  // hide/report/edit/delete flows so the menu component stays dumb.
  // Native share is used when available; otherwise we fall back to a
  // clipboard copy with a localized toast.
  const handleShareAd = useCallback(async () => {
    if (!ad) return;
    const url = buildShareUrl(`/ad/${ad.ad_id || adId}`);
    const shareTitle = ad.title || 'Wassla';
    const shareText = isArabic
      ? `شاهد هذا الإعلان على وصلة: ${shareTitle}`
      : `Check out this ad on Wassla: ${shareTitle}`;
    const res = await shareNative({ title: shareTitle, text: shareText, url });
    if (res.completed) return;
    // Fallback: copy the public link to clipboard.
    try {
      await copyText(url);
      toast.success(isArabic ? 'تم نسخ رابط الإعلان' : 'Ad link copied');
    } catch {
      toast.error(isArabic ? 'تعذّر نسخ الرابط' : 'Could not copy link');
    }
  }, [ad, adId, isArabic]);

  const handleHideFromMenu = useCallback(() => {
    setShowHideConfirm(true);
  }, []);

  const handleReportFromMenu = useCallback(() => {
    if (hasReported) {
      toast.info(t('report.already_reported'));
      return;
    }
    setShowReportSheet(true);
  }, [hasReported, t]);

  const handleEditAd = useCallback(() => {
    if (!ad) return;
    // Existing edit flow lives on /#/edit-ad/:adId — reuse it.
    navigate(`/edit-ad/${ad.ad_id || adId}`);
  }, [ad, adId, navigate]);

  const handlePauseAd = useCallback(async () => {
    if (!ad) return;
    try {
      const authToken = localStorage.getItem('token');
      // iter331 — "Pause listing" reuses the existing
      // POST /api/ads/{id}/hide endpoint, which sets the canonical
      // `hidden_by_user` status (i.e. seller-paused, restoreable).
      // No new business logic — we're just surfacing the existing
      // hide flow as a "Pause" menu item for owners.
      const res = await fetch(`${API_URL}/api/ads/${ad.ad_id || adId}/hide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });
      if (!res.ok) throw new Error('pause-failed');
      toast.success(isArabic ? 'تم إيقاف الإعلان مؤقتاً' : 'Listing paused');
      setAd((prev) => prev ? { ...prev, status: 'hidden_by_user' } : prev);
    } catch {
      toast.error(isArabic ? 'تعذّر إيقاف الإعلان' : 'Could not pause listing');
    }
  }, [ad, adId, isArabic]);

  const handleDeleteAd = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const confirmDeleteAd = useCallback(async () => {
    if (!ad) return;
    try {
      const authToken = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ads/${ad.ad_id || adId}`, {
        method: 'DELETE',
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!res.ok) throw new Error('delete-failed');
      toast.success(isArabic ? 'تم حذف الإعلان' : 'Ad deleted');
      setShowDeleteConfirm(false);
      if (hasInAppHistory()) navigate(-1);
      else navigate('/my-ads', { replace: true });
    } catch {
      toast.error(isArabic ? 'تعذّر حذف الإعلان' : 'Could not delete ad');
    }
  }, [ad, adId, isArabic, navigate]);
  
  // Check if ad is unavailable (sold, expired, or explicitly unavailable)
  const isUnavailable = ad && ['sold', 'expired', 'unavailable'].includes(ad.availability_status);
  
  // Check if ad is suspected unavailable
  const isSuspectedUnavailable = ad && ad.availability_status === 'suspected_unavailable';

  // Last active helper
  const sellerLastActive = React.useMemo(() => {
    const ts = ad?.seller?.last_login_at;
    if (!ts) return null;
    try {
      const diffDays = (Date.now() - new Date(ts).getTime()) / 86400000;
      if (diffDays < 1) return t('adDetails.activeToday');
      if (diffDays < 7) return t('adDetails.activeThisWeek');
      if (diffDays < 30) return t('adDetails.activeRecently');
      return null;
    } catch { return null; }
  }, [ad?.seller?.last_login_at, t]);

  /**
   * Seller response hint — derived entirely from data already present
   * on the ad-details payload's seller subdoc. The backend handler
   * inlines the same computation the standalone
   * `/api/seller/{id}/response-score` endpoint returns, so NO extra
   * client request is made. Thresholds mirror the legacy SellerProfile
   * page so a seller gets the same label on both surfaces.
   */
  const sellerResponseHint = React.useMemo(() => {
    const score = ad?.seller?.response_score;
    if (typeof score !== 'number' || score < 40) return null;
    if (score >= 80) return t('adDetails.responseVeryFast');
    if (score >= 60) return t('adDetails.responseFast');
    return t('adDetails.responseHours');
  }, [ad?.seller?.response_score, t]);

  // Profile completeness — ONE shared source of truth (see profileStrength.js).
  // We pass `ad.seller` UNMODIFIED to the helper so SellerProfile (which
  // reads the same `/api/seller/{id}` shape) and AdDetails compute the
  // exact same percentage. A seller's profile strength must NOT inherit
  // an ad-specific location — that would diverge from the public profile
  // page and confuse users (50% here, 40% there).
  const sellerCompleteness = React.useMemo(() => {
    return computeProfileStrength(ad?.seller);
  }, [ad?.seller]);

  // Get availability status for display
  const getAvailabilityLabel = () => {
    if (!ad) return '';
    switch (ad.availability_status) {
      case 'sold': return t('adDetails.sold');
      case 'expired': return t('adDetails.expired');
      case 'unavailable': return t('adDetails.unavailable');
      case 'reserved': return t('adDetails.reserved');
      default: return t('adDetails.available');
    }
  };

  // Register ad view on mount (with deduplication)
  useRegisterAdView(adId, ad?.viewCount ?? 0, setViewCount);
  
  // Analytics: track listing view
  useEffect(() => {
    if (adId) {
      trackListingView(adId);
      // Track recently viewed (localStorage-based, per-user).
      // Never write when auth hasn't resolved — the util itself rejects
      // anonymous/empty user IDs, but we guard at the call site too so
      // the write simply doesn't happen until a real user is known.
      const _uid = user?.user_id || user?.id;
      if (_uid) {
        addRecentlyViewed(adId, _uid);
      }
    }
  }, [adId]);
  
  // Phone safety check
  useEffect(() => {
    if (!ad || !ad.category_id) return;
    const cat = (ad.category_id || '').toLowerCase();
    if (!cat.includes('mobile') && !cat.includes('phone') && !cat.includes('electronic')) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/phones/safety-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ price: ad.price, phone_attrs: ad.phone_attrs, images: ad.images, category_id: ad.category_id }),
    })
      .then(r => r.json())
      .then(data => { if (data.buyer_warning) setPriceWarning(true); })
      .catch(() => {});
  }, [ad]);

  // Sync viewCount from ad data
  useEffect(() => {
    if (ad?.viewCount != null) setViewCount(ad.viewCount);
  }, [ad?.viewCount]);

  // Fetch price cache for price insight
  // Disabled for categories where comparables are unreliable (wide price variance, no make/model matching)
  const PRICE_INSIGHT_DISABLED_CATEGORIES = [
    'vehicles', 'vehicles_motorbikes', 'cars', 'motorbikes',
    'real_estate', 'properties', 'land', 'apartments', 'houses',
    'jobs', 'services', 'freelance',
  ];
  const isPriceInsightEnabled = ad?.category_id &&
    !PRICE_INSIGHT_DISABLED_CATEGORIES.some(c =>
      (ad.category_id || '').toLowerCase().includes(c) ||
      (ad.subcategory_id || '').toLowerCase().includes(c) ||
      (ad.category || '').toLowerCase().includes(c)
    );

  useEffect(() => {
    if (!SHOW_PRICE_INSIGHT) return;
    if (!isPriceInsightEnabled || !ad?.price) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/ads?category=${ad.category_id}&limit=50`)
      .then(r => r.json())
      .then(data => {
        const prices = (data.ads || []).map(a => a.price).filter(p => p && p > 0);
        if (prices.length >= 15) setPriceCache(prices);
      })
      .catch(() => {});
  }, [isPriceInsightEnabled, ad?.category_id, ad?.price]);

  // Fetch seller's other ads for "More from this seller" section
  useEffect(() => {
    const sellerId = ad?.user_id || ad?.seller?.user_id;
    if (!sellerId || !adId) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/seller/${sellerId}/ads?limit=5&exclude=${adId}`)
      .then(r => r.ok ? r.json() : { ads: [] })
      .then(data => setSellerAds((data.ads || []).slice(0, 4)))
      .catch(() => {});
  }, [ad?.user_id, ad?.seller?.user_id, adId]);

  // Handle back navigation — pop real history when it exists; fall back to
  // returnTo (or /shop) ONLY for deep links with no in-app history.
  const handleBack = () => {
    if (hasInAppHistory()) return navigate(-1);
    navigate(location.state?.returnTo || '/shop', { replace: true });
  };

  useEffect(() => {
    fetchAd();
    checkReportStatus();
  }, [adId]);

  // Check if user has already reported this ad (unified: availability OR policy)
  const checkReportStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await fetch(`${API_URL}/api/ads/${adId}/report-status`, {
        headers,
      });
      
      if (response.ok) {
        const data = await response.json();
        setHasReported(data.has_reported);
      }
    } catch (error) {
      console.error('Failed to check report status:', error);
    }
  };

  const fetchAd = async () => {
    try {
      // Always send Authorization header when available so the backend can
      // serve non-public statuses (pending_review / hidden / rejected /
      // under_review / moderation) to the OWNER or to admins. Without this
      // header an owner opening their own "Moderation" ad from My Ads hit a
      // 403 and saw a misleading "Ad not found" message. (Fix 2026-06-01e)
      const tk = localStorage.getItem('token');
      const headers = tk ? { Authorization: `Bearer ${tk}` } : {};
      const response = await fetch(`${API_URL}/api/ads/${adId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAd(data);
      } else {
        setError(true);
      }
    } catch (error) {
      console.error('Failed to fetch ad:', error);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Get proper image URL with backend prefix if needed, applying trust guard
  // iter275 — delegate path resolution to canonical resolver so same-origin
  // fallback applies on production custom domains (wassla.tech).
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    const url = toAbsoluteUrl(imagePath);
    return guardImage(url, ad?.category_id || ad?.category);
  };

  // Images — canonical helper (2026-06-01f).
  // Reads from images / imageUrls / photos / thumbnail (in priority), returns
  // ABSOLUTE deduped URLs. Identical extraction logic to MyAds card so the
  // photo shown on the listing card is guaranteed to be the photo rendered
  // on this page. Owner / admin / public visitor — the array contents only
  // depend on the ad doc, not on viewer role.
  const images = normalizeAdImages(ad);

  const titleText = ad ? (getAdTitle(ad, lang).text || ad.title) : '';
  const descriptionText = ad
    ? (getAdDescription(ad, lang).text || ad.description || t('adDetails.noDescription'))
    : '';
  const locationText = ad ? getLocalizedLocationLabel(ad, lang) : '';
  const categoryText = ad ? getAdCategoryLabel(ad, lang) : '';

  const discountPercent = Number(ad?.discount_percent || 0);
  const hasDiscount = Number.isFinite(discountPercent) && discountPercent > 0 && discountPercent < 100 && Number(ad?.price) > 0;
  const oldPriceAmount = hasDiscount ? Number(ad.price) / (1 - discountPercent / 100) : null;

  // iter403 — auto "price drop" display removed: a crossed-out old price
  // and percent badge come ONLY from an explicit seller discount.

  const phoneRaw = ad?.seller_phone || ad?.seller?.phone || '';
  const phoneNorm = normalizeInternationalPhone(phoneRaw);

  const waRaw = ad?.seller_whatsapp || ad?.seller?.whatsapp_phone || phoneRaw;
  // iter314 — Build the WhatsApp link via the country-neutral
  // normalizer so Sudan local formats keep auto-prefixing to +249
  // AND multi-country numbers (+20, +1, +44, …) also produce a
  // working wa.me link.
  const waNorm = normalizeInternationalPhone(waRaw);
  const waDigits = waNorm.valid ? waNorm.waDigits : '';
  const hasWhatsApp = waNorm.valid;

  const chatMessage = encodeURIComponent(
    t('adDetails.whatsappMessage', { title: titleText })
  );
  const whatsappHref = hasWhatsApp ? `https://wa.me/${waDigits}?text=${chatMessage}` : '';
  const hasPrimaryWhatsAppCTA = Boolean(whatsappHref);

  const sellerJoinedText = ad?.seller?.joined_at
    ? formatMemberSince(ad.seller.joined_at, locale)
    : null;

  const handleStickyAction = (action) => {
    if (!user) {
      toast.info(t('adDetails.signInToContact'));
      navigate('/auth', { state: { from: { pathname: location.pathname } } });
      return;
    }

    // iter349 — Record every real buyer→seller contact click into
    // db.contact_logs. This is what powers review eligibility (a
    // buyer can only rate a seller they actually contacted) as well
    // as the seller's My Business Responses feed. Fire-and-forget:
    // the network call must never block or defer the UX that follows.
    const logContact = (channel) => {
      if (!adId || isOwner) return;
      try {
        const tok = (typeof window !== 'undefined') ? localStorage.getItem('token') : '';
        const headers = { 'Content-Type': 'application/json' };
        if (tok) headers.Authorization = `Bearer ${tok}`;
        fetch(`${API_URL}/api/ads/${adId}/contact`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ ad_id: adId, contact_type: channel }),
        })
          .then((r) => { if (r.ok) refreshReviewEligibility?.(); })
          .catch(() => { /* swallowed */ });
      } catch (_) { /* swallowed */ }
    };

    if (action === 'whatsapp') {
      if (hasPrimaryWhatsAppCTA) {
        logContact('whatsapp');
        window.open(whatsappHref, '_blank', 'noopener,noreferrer');
      } else {
        // iter310 — friendly fallback when seller WhatsApp is missing
        // or unparseable. Keeps the same toast surface; no crash.
        toast.error(
          isArabic
            ? 'رقم واتساب البائع غير متوفر أو غير صحيح.'
            : 'Seller WhatsApp number is not available or invalid.'
        );
      }
      return;
    }

    if (action === 'message') {
      logContact('message');
      // Open in-app messaging with seller context
      navigate('/messages/new', { state: {
        fromAdId: adId,
        sellerName: ad?.seller_name || ad?.seller?.name || '',
        sellerId: ad?.user_id || '',
        adTitle: ad?.title || '',
      }});
      return;
    }

    if (action === 'call') {
      logContact('call');
      // Existing tel: handling elsewhere (if applicable)
      return;
    }

    if (action === 'save') {
      const wasAlreadySaved = isFavorite(adId);
      toggleFavorite(adId, {
        preventDefault: () => {},
        stopPropagation: () => {},
      });
      // Show toast only when saving (not unsaving)
      if (!wasAlreadySaved) {
        toast.success(t('adDetails.savedToFavorites'));
      }
    }
  };

  const handleViewSeller = () => {
    const sellerId = ad?.user_id || ad?.seller?.user_id;
    if (sellerId) {
      navigate(`/seller/${sellerId}`);
    }
  };

  if (loading) {
    return (
      <div className="ad-details-page py-6">
        <div className="container">
          <div className="loading-state">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !ad) {
    return (
      <div className="ad-details-page">
        <AppHeader
          title={t('adDetails.adNotFound')}
          onBack={handleBack}
          testId="ad-details-header"
        />
        <div className="container py-6">
          {error ? (
            <InlineError onRetry={() => { setError(false); setLoading(true); fetchAd(); }} />
          ) : (
            <div className="empty-state">
              <p>{t('adDetails.adNotFound')}</p>
              <button onClick={() => navigate('/shop')} className="btn btn-primary">
                {t('shop')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ad-details-page conversion-layout">
      <AppHeader
        title={titleText}
        onBack={handleBack}
        testId="ad-details-header"
      />
      <div className="ad-details-content-shell">
        {/* Unavailability Banner */}
        {isUnavailable && (
          <div className="unavailable-banner" data-testid="unavailable-banner">
            <div className="unavailable-banner-icon">
              <AlertCircle size={24} />
            </div>
            <div className="unavailable-banner-content">
              <h3 className="unavailable-banner-title">
                {t('adDetails.notAvailable')}
              </h3>
              <p className="unavailable-banner-text">
                {t('adDetails.soldOrExpiredBanner')}
              </p>
            </div>
          </div>
        )}
      </div>

      <section className="ad-details-hero-gallery" data-testid="ad-hero-gallery" style={{ position: 'relative' }}>
        <AdImageGallery
          images={images}
          title={titleText}
          hero={true}
          showThumbnails={false}
        />
        {/* iter331 — Three-dot ⋮ actions menu, top-start corner on
            the hero (heart lives top-end). Owner sees Edit / Pause /
            Delete; non-owner sees Share / Hide / Report. Existing
            handlers reused — this is purely UI relocation. */}
        <AdActionsMenu
          isOwner={isOwner}
          isArabic={isArabic}
          hasReported={hasReported}
          onShare={handleShareAd}
          onHide={handleHideFromMenu}
          onReport={handleReportFromMenu}
          onEdit={handleEditAd}
          onPause={handlePauseAd}
          onDelete={handleDeleteAd}
        />
        {/* Favorite heart overlay on photo */}
        <button
          className="ad-gallery-fav-btn"
          onClick={(e) => { e.stopPropagation(); toggleFavorite(adId, e); }}
          data-testid="gallery-favorite-btn"
          aria-label={isFavorite(adId) ? 'Remove from favorites' : 'Save to favorites'}
        >
          <Heart size={20} fill={isFavorite(adId) ? 'currentColor' : 'none'} />
        </button>
      </section>

      <div className="ad-details-content-shell">
        <div className="ad-details-container">
          <div className="ad-info">
            {/* Phone price warning banner */}
            {priceWarning && (
              <div className="phone-price-warning" data-testid="phone-price-warning">
                <span className="phone-price-warning-icon">⚠</span>
                <span>{t('adDetails.priceWarning')}</span>
              </div>
            )}
            {ad.availability_status && ad.availability_status !== 'available' && (
              <div className="ad-availability-badge-row">
                <AvailabilityBadge
                  status={ad.availability_status}
                  reserved_until={ad.reserved_until}
                  last_refreshed_at={ad.last_refreshed_at}
                  category={ad.category}
                  size="md"
                  showStaleHint={true}
                />
              </div>
            )}

            <div className="ad-price-block" data-testid="ad-price-block">
              <h1 className="ad-details-title" data-testid="ad-details-title">{titleText}</h1>
              <p className="ad-details-price" data-testid="ad-price">
                {ad.price != null
                  ? formatMoney({ amount: ad.price, currency: ad.currency || 'SDG', lang: isArabic ? 'ar' : 'en' })
                  : t('adDetails.priceOnRequest')}
              </p>
              {hasDiscount && oldPriceAmount && (
                <div className="ad-price-secondary" data-testid="ad-price-secondary">
                  <span className="ad-old-price">
                    {formatMoney({ amount: oldPriceAmount, currency: ad.currency || 'SDG', lang: isArabic ? 'ar' : 'en' })}
                  </span>
                  <span className="ad-discount-badge" data-testid="ad-discount-badge">-{Math.round(discountPercent)}%</span>
                </div>
              )}
            </div>

            {/* Listing Facts Row */}
            <div className="ad-facts-row" data-testid="ad-facts-row">
              {!!locationText && (
                <div className="ad-fact">
                  <MapPin size={14} />
                  <span>{locationText}</span>
                </div>
              )}
              {ad?.created_at && (
                <div className="ad-fact">
                  <Clock3 size={14} />
                  <span>{timeAgo}</span>
                </div>
              )}
              {ad.condition && (
                <div className="ad-fact">
                  <span className="ad-fact-label">{t('adDetails.condition')}</span>
                  <span>{ad.condition}</span>
                </div>
              )}
              {(ad.price_negotiable || ad.negotiable) && (
                <div className="ad-fact ad-fact-green">
                  <span>{t('adDetails.negotiable')}</span>
                </div>
              )}
              <div className="ad-fact">
                <Eye size={14} />
                <span>{viewCount} {t('adDetails.views')}</span>
              </div>
              {ad.category_id && (
                <div className="ad-fact ad-fact-cat">
                  <span>{getAdCategoryLabel(ad, isArabic ? 'ar' : 'en') || ad.category_id?.replace(/_/g, ' ')}</span>
                </div>
              )}
            </div>

            {/* Price Insight — dormant for MVP. Gated by SHOW_PRICE_INSIGHT (false). */}
            {SHOW_PRICE_INSIGHT && isPriceInsightEnabled && ad.price > 0 && priceCache && priceCache.length >= 15 && (() => {
              const prices = [...priceCache].sort((a, b) => a - b);
              const median = prices[Math.floor(prices.length / 2)];
              const low = prices[Math.floor(prices.length * 0.25)];
              const high = prices[Math.floor(prices.length * 0.75)];
              const ratio = ad.price / median;
              let label, labelClass;
              if (ratio < 0.7) { label = t('adDetails.belowTypical'); labelClass = 'pi-low'; }
              else if (ratio > 1.3) { label = t('adDetails.aboveTypical'); labelClass = 'pi-high'; }
              else { label = t('adDetails.fairPrice'); labelClass = 'pi-fair'; }
              return (
                <div className="ad-price-insight" data-testid="price-insight">
                  <div className="pi-header">
                    <span className="pi-title">{t('adDetails.priceInsight')}</span>
                    <span className={`pi-label ${labelClass}`}>{label}</span>
                  </div>
                  <div className="pi-range">
                    <span>{t('adDetails.typicalRange')}: </span>
                    <strong>{formatMoney({amount: low, currency: 'SDG', lang: isArabic ? 'ar' : 'en'})} – {formatMoney({amount: high, currency: 'SDG', lang: isArabic ? 'ar' : 'en'})}</strong>
                  </div>
                </div>
              );
            })()}

            {/* iter302 — Compact 3-bullet safety card on Ad Details.
                Replaces the single safetyTipBody string. "View all
                safety tips" opens the full 5-bullet list in a modal. */}
            <SafetyTipsCard variant="compact" />

            <div className="ad-trust-strip" data-testid="ad-trust-strip">
              <div className="trust-strip-meta">
                {isSellerVerified(ad.seller) && (
                  <span className="trust-item" data-testid="trust-verified-item">
                    <PremiumVerifiedBadge verified={true} lang={lang} />
                  </span>
                )}
              </div>
              {/* iter331 — Report / Hide buttons relocated to the
                  three-dot ⋮ menu on the hero gallery. Existing
                  handlers (setShowReportSheet / setShowHideConfirm)
                  remain — only the UI surface moved. The trust-strip
                  now exclusively shows positive signals so it never
                  competes with the safety / report affordance. */}
            </div>

            {/* Hide Confirmation Dialog */}
            <AlertDialog open={showHideConfirm} onOpenChange={setShowHideConfirm}>
              <AlertDialogContent dir={isArabic ? 'rtl' : 'ltr'} className="hide-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('adDetails.hideConfirmTitle')}</AlertDialogTitle>
                  <AlertDialogDescription>{t('adDetails.hideConfirmSubtext')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className={`modalActions ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <AlertDialogCancel className="btn btnSecondaryOrange" data-testid="hide-cancel-btn">
                    {t('adDetails.hideCancel')}
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => {
                      hideListing(adId, user?.user_id || user?.id);
                      toast.success(t('adDetails.listingHidden'));
                      if (hasInAppHistory()) navigate(-1);
                      else navigate('/shop', { replace: true });
                    }} 
                    className="btn btnPrimaryOrange"
                    data-testid="hide-confirm-btn"
                  >
                    {t('adDetails.hideConfirmBtn')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* iter331 — Owner-only Delete Ad confirmation, triggered
                from the three-dot ⋮ menu on the hero. Existing
                DELETE /api/ads/{ad_id} endpoint is untouched. */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <AlertDialogContent dir={isArabic ? 'rtl' : 'ltr'} className="hide-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {isArabic ? 'حذف الإعلان؟' : 'Delete this ad?'}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isArabic
                      ? 'لا يمكن التراجع عن هذا الإجراء. سيتم حذف الإعلان نهائياً.'
                      : 'This cannot be undone. The ad will be permanently removed.'}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className={`modalActions ${isArabic ? 'flex-row-reverse' : ''}`}>
                  <AlertDialogCancel className="btn btnSecondaryOrange" data-testid="delete-cancel-btn">
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmDeleteAd}
                    className="btn"
                    style={{ background: '#dc2626', color: '#fff' }}
                    data-testid="delete-confirm-btn"
                  >
                    {isArabic ? 'حذف' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* iter288 — Service Details panel. Renders when the ad is
                listing_type='service'. Operator spec: show service
                category + subcategory + price-type + optional fields
                (experience, specializes_in, brand_specialty, home_visit,
                emergency, service_area, availability). Replaces the
                Vehicle Details panel for service ads even if their
                underlying `category_path` looks vehicle-ish. */}
            {ad.listing_type === 'service' && (() => {
              const opts = ad.service_options || {};
              const PRICE_TYPE_LABELS = {
                fixed:             { en: 'Fixed price',       ar: 'سعر ثابت' },
                starting_from:     { en: 'Starting from',     ar: 'ابتداءً من' },
                negotiable:        { en: 'Negotiable',        ar: 'قابل للتفاوض' },
                free_consultation: { en: 'Free consultation', ar: 'استشارة مجانية' },
              };
              // Look up the bilingual labels from the canonical taxonomy
              // via a small lookup table built from /api/services/catalog.
              // For now we use the snake_case id as a fallback so a
              // freshly-published service ad never renders empty.
              const labelize = (s) =>
                s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
              const rows = [];
              if (ad.service_category) {
                rows.push({
                  k: isArabic ? 'فئة الخدمة' : 'Service Category',
                  v: labelize(ad.service_category),
                  testid: 'ad-service-category',
                });
              }
              if (ad.service_subcategory) {
                rows.push({
                  k: isArabic ? 'نوع الخدمة' : 'Service Type',
                  v: labelize(ad.service_subcategory),
                  testid: 'ad-service-subcategory',
                });
              }
              if (ad.price_type && PRICE_TYPE_LABELS[ad.price_type]) {
                rows.push({
                  k: isArabic ? 'نوع السعر' : 'Price Type',
                  v: isArabic ? PRICE_TYPE_LABELS[ad.price_type].ar : PRICE_TYPE_LABELS[ad.price_type].en,
                  testid: 'ad-service-price-type',
                });
              }
              if (opts.experience_years) {
                rows.push({
                  k: isArabic ? 'سنوات الخبرة' : 'Experience (years)',
                  v: opts.experience_years,
                  testid: 'ad-service-experience',
                });
              }
              if (opts.specializes_in) {
                rows.push({
                  k: isArabic ? 'متخصص في' : 'Specializes in',
                  v: labelize(opts.specializes_in),
                  testid: 'ad-service-specializes-in',
                });
              }
              if (opts.brand_specialty) {
                rows.push({
                  k: isArabic ? 'العلامات التجارية' : 'Brand specialty',
                  v: opts.brand_specialty,
                  testid: 'ad-service-brand-specialty',
                });
              }
              if (opts.service_area) {
                rows.push({
                  k: isArabic ? 'منطقة الخدمة' : 'Service Area',
                  v: opts.service_area,
                  testid: 'ad-service-area',
                });
              }
              if (opts.availability) {
                rows.push({
                  k: isArabic ? 'أوقات العمل' : 'Availability',
                  v: opts.availability,
                  testid: 'ad-service-availability',
                });
              }
              if (opts.home_visit) {
                rows.push({
                  k: isArabic ? 'يقدّم الخدمة بالمنزل' : 'Home Visit',
                  v: isArabic ? 'نعم' : 'Yes',
                  testid: 'ad-service-home-visit',
                });
              }
              if (opts.emergency) {
                rows.push({
                  k: isArabic ? 'خدمة طوارئ' : 'Emergency Service',
                  v: isArabic ? 'نعم' : 'Yes',
                  testid: 'ad-service-emergency',
                });
              }
              if (rows.length === 0) return null;
              return (
                <div className="ad-description-section" data-testid="ad-service-section">
                  <h3 className="section-subtitle">
                    {isArabic ? 'تفاصيل الخدمة' : 'Service Details'}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 14 }}>
                    {rows.map((row, idx) => (
                      <div key={idx} data-testid={row.testid} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ color: 'var(--muted, #6b7280)', fontSize: 12 }}>{row.k}</span>
                        <span style={{ fontWeight: 500 }}>{row.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* iter287 — Vehicle specs panel. Renders when the ad has
                car_attrs (set by the Vehicles & Motorbikes posting flow).
                Shows ONLY the fields that are populated — never displays
                placeholder "—" rows. Vehicle Type is surfaced first
                because the operator wants to see it on every vehicle ad.
                iter288 — explicitly skipped when listing_type='service'
                so a mechanic service never renders as a vehicle product. */}
            {ad.listing_type !== 'service' && ad.car_attrs && (ad.car_attrs.vehicle_type || ad.car_attrs.brand || ad.car_attrs.model || ad.car_attrs.year) && (() => {
              const ca = ad.car_attrs;
              const VEHICLE_TYPE_LABELS = {
                cars: { en: 'Cars', ar: 'سيارات' },
                motorbikes: { en: 'Motorbikes', ar: 'دراجات نارية' },
                tuktuk: { en: 'Tuk-tuk / Rickshaw', ar: 'ركشة' },
                trucks: { en: 'Trucks / Heavy Vehicles', ar: 'شاحنات / آليات ثقيلة' },
                spare_parts: { en: 'Spare Parts', ar: 'قطع غيار' },
              };
              const vtLabel = VEHICLE_TYPE_LABELS[ca.vehicle_type];
              const rows = [];
              if (vtLabel) {
                rows.push({
                  k: isArabic ? 'نوع المركبة' : 'Vehicle Type',
                  v: isArabic ? vtLabel.ar : vtLabel.en,
                  testid: 'ad-vehicle-type',
                });
              }
              const brandVal = ca.brand_other || ca.brand;
              if (brandVal) rows.push({ k: isArabic ? 'العلامة التجارية' : 'Brand', v: brandVal, testid: 'ad-vehicle-brand' });
              const modelVal = ca.model_other || ca.model;
              if (modelVal) rows.push({ k: isArabic ? 'الموديل' : 'Model', v: modelVal, testid: 'ad-vehicle-model' });
              if (ca.year) rows.push({ k: isArabic ? 'سنة الصنع' : 'Year', v: ca.year, testid: 'ad-vehicle-year' });
              if (ca.mileage) rows.push({ k: isArabic ? 'المسافة المقطوعة' : 'Mileage', v: ca.mileage, testid: 'ad-vehicle-mileage' });
              if (ca.fuel) rows.push({ k: isArabic ? 'الوقود' : 'Fuel', v: ca.fuel, testid: 'ad-vehicle-fuel' });
              if (ca.transmission) rows.push({ k: isArabic ? 'ناقل الحركة' : 'Transmission', v: ca.transmission, testid: 'ad-vehicle-transmission' });
              if (ca.body_type) rows.push({ k: isArabic ? 'الفئة' : 'Body Type', v: ca.body_type, testid: 'ad-vehicle-body' });
              if (ca.condition) rows.push({ k: isArabic ? 'الحالة' : 'Condition', v: ca.condition, testid: 'ad-vehicle-condition' });
              if (rows.length === 0) return null;
              return (
                <div className="ad-description-section" data-testid="ad-vehicle-specs-section">
                  <h3 className="section-subtitle">
                    {isArabic ? 'تفاصيل المركبة' : 'Vehicle Details'}
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 14 }}>
                    {rows.map((row, idx) => (
                      <div key={idx} data-testid={row.testid} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ color: 'var(--muted, #6b7280)', fontSize: 12 }}>{row.k}</span>
                        <span style={{ fontWeight: 500 }}>{row.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="ad-description-section" data-testid="ad-description-section">
              <h3 className="section-subtitle">{t('adDetails.descriptionTitle')}</h3>
              <p
                className={`ad-description-body ${showFullDescription ? 'expanded' : 'collapsed'}`}
                data-testid="ad-description-body"
              >
                {descriptionText}
              </p>
              {descriptionText.length > 120 && (
                <button
                  type="button"
                  className="ad-description-toggle"
                  onClick={() => setShowFullDescription((s) => !s)}
                  data-testid="ad-description-toggle"
                >
                  {showFullDescription
                    ? t('adDetails.readLess')
                    : t('adDetails.readMore')}
                </button>
              )}
            </div>

            {/* iter328 — Seller-provided Delivery section. Wassla is NOT
                the courier; this is purely the seller's stated delivery
                availability. Buyers MUST arrange delivery directly with
                the seller, which is reinforced by the safety note. */}
            <div className="ad-delivery-section" data-testid="ad-delivery-section">
              <h3 className="section-subtitle">
                {isArabic ? 'التوصيل' : 'Delivery'}
              </h3>
              {ad.delivery_available ? (
                <>
                  <div className="ad-delivery-status ad-delivery-status--on" data-testid="ad-delivery-status-on">
                    <Truck size={16} />
                    <span>{isArabic ? 'التوصيل متاح' : 'Delivery available'}</span>
                  </div>
                  <ul className="ad-delivery-meta">
                    {ad.delivery_type === 'free' && (
                      <li data-testid="ad-delivery-fee">
                        <strong>{isArabic ? 'الرسوم: ' : 'Fee: '}</strong>
                        {isArabic ? 'مجاني' : 'Free'}
                      </li>
                    )}
                    {ad.delivery_type === 'paid' && ad.delivery_fee_amount != null && (
                      <li data-testid="ad-delivery-fee">
                        <strong>{isArabic ? 'الرسوم: ' : 'Fee: '}</strong>
                        {`${ad.delivery_fee_currency || 'SDG'} ${new Intl.NumberFormat(isArabic ? 'ar-SD' : 'en', { maximumFractionDigits: 0 }).format(ad.delivery_fee_amount)}`}
                      </li>
                    )}
                    {ad.delivery_type === 'depends_on_location' && (
                      <li data-testid="ad-delivery-fee">
                        <strong>{isArabic ? 'الرسوم: ' : 'Fee: '}</strong>
                        {isArabic ? 'حسب الموقع' : 'Depends on location'}
                      </li>
                    )}
                    {ad.delivery_area && (
                      <li data-testid="ad-delivery-area">
                        <strong>{isArabic ? 'منطقة التوصيل: ' : 'Delivery area: '}</strong>
                        {ad.delivery_area}
                      </li>
                    )}
                  </ul>
                  <p className="ad-delivery-safety" data-testid="ad-delivery-safety-note">
                    {isArabic
                      ? '⚠️ يتم ترتيب التوصيل مباشرة مع البائع. تأكد من الرسوم والموقع قبل الدفع.'
                      : '⚠️ Delivery is arranged directly with the seller. Confirm cost and location before payment.'}
                  </p>
                </>
              ) : (
                <div className="ad-delivery-status ad-delivery-status--off" data-testid="ad-delivery-status-off">
                  <Package size={16} />
                  <span>
                    {isArabic
                      ? 'لا يوجد توصيل — يستلم المشتري بنفسه'
                      : 'No delivery — buyer collects'}
                  </span>
                </div>
              )}
            </div>

            {ad.seller && (
              <div className="seller-info-preview" data-testid="seller-info-card">
                <div className="seller-avatar">
                  {ad.seller.avatar && !sellerAvatarFailed ? (
                    <img
                      src={getImageUrl(ad.seller.avatar)}
                      alt=""
                      onError={() => setSellerAvatarFailed(true)}
                      data-testid="addetails-seller-avatar-img"
                    />
                  ) : ad.seller.name ? (
                    <span className="seller-avatar-initial" data-testid="addetails-seller-avatar-fallback">
                      {ad.seller.name.trim().charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <UserCircle2 size={32} />
                  )}
                </div>
                <div className="seller-info-text">
                  <div className="seller-name-row">
                    <span className="seller-name">{ad.seller.name || t('adDetails.seller')}</span>
                    {isSellerVerified(ad.seller) && (
                      <PremiumVerifiedBadge verified={true} lang={lang} />
                    )}
                  </div>
                  <span className="seller-member-since" data-testid="seller-member-since">
                    {t('adDetails.memberSince')} {sellerJoinedText || t('adDetails.notAvailableShort')}
                  </span>
                  {getSellerActiveAdCount(ad.seller) > 0 && (
                    <span className="seller-ad-count" data-testid="seller-ad-count">
                      {getSellerActiveAdCount(ad.seller)} {t('adDetails.activeListings')}
                    </span>
                  )}
                  {/* iter317 — Inline rating badge. Tap opens reviews sheet. */}
                  {(ad?.user_id || ad?.seller?.user_id) && (
                    <span className="seller-rating-badge-wrap" style={{ marginTop: 4, display: 'inline-flex' }}>
                      <SellerRatingBadge
                        sellerUserId={ad?.user_id || ad?.seller?.user_id}
                        onOpen={() => setShowReviewsSheet(true)}
                      />
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="seller-view-profile-btn"
                  onClick={handleViewSeller}
                  data-testid="seller-view-profile-btn"
                  disabled={!(ad?.user_id || ad?.seller?.user_id)}
                >
                  {t('adDetails.viewProfile')}
                </button>
              </div>
            )}

            {/* Contact Social Proof */}
            {ad.metrics?.contact_clicks > 0 && (
              <div className="contact-social-proof" data-testid="contact-social-proof">
                <Users size={14} />
                <span>
                  {t('adDetails.peopleContacted', { count: ad.metrics.contact_clicks })}
                </span>
              </div>
            )}

            {/* Seller Trust Layer — ordered: age → badges → counts */}
            {ad.seller && (() => {
              const seller = ad.seller;
              const hasPhone = seller.phoneVerified || seller.phone_verified || !!seller.phone;
              const hasEmail = !!seller.email || !!ad.seller_email || !!seller.is_verified;
              const isVerified = isSellerVerified(seller);
              const memberDate = formatMemberSince(seller.joined_at, locale);
              const activeCount = getSellerActiveAdCount(seller);

              // Account age logic for anti-scam warnings
              let accountAgeDays = null;
              let showNewWarning = false; // <7 days
              let showCautionWarning = false; // <3 days
              if (seller.joined_at) {
                try {
                  const joined = new Date(seller.joined_at);
                  accountAgeDays = Math.floor((Date.now() - joined.getTime()) / 86400000);
                  if (accountAgeDays < 7) showNewWarning = true;
                  if (accountAgeDays < 3) showCautionWarning = true;
                } catch {}
              }

              return (
                <div className="seller-trust-layer" data-testid="seller-trust-layer">
                  <p className="stl-heading">{isArabic ? 'معلومات البائع' : 'Seller Information'}</p>

                  {/* Anti-scam warnings */}
                  {showCautionWarning && (
                    <div className="stl-caution" data-testid="seller-caution-warning">
                      <AlertTriangle size={14} />
                      <span>{isArabic ? 'بائع جديد — تعامل بحذر' : 'New seller — trade carefully'}</span>
                    </div>
                  )}
                  {showNewWarning && !showCautionWarning && (
                    <div className="stl-new-badge" data-testid="seller-new-warning">
                      <Clock size={12} />
                      <span>{isArabic ? 'حساب بائع جديد' : 'New seller account'}</span>
                    </div>
                  )}

                  {/* 1. Member since */}
                  {memberDate && (
                    <div className="stl-row" data-testid="stl-member-since">
                      <Clock size={13} className="stl-icon" />
                      <span>{t('adDetails.memberSince')} {memberDate}</span>
                    </div>
                  )}

                  {/* 1b. Seller active status — reuses the sellerLastActive
                         memo already computed at the top of this component
                         (previously dead code). Pure rendering, no new data,
                         no new API call. Shares the existing .stl-row layout
                         so it inherits spacing, alignment and RTL behaviour. */}
                  {sellerLastActive && (
                    <div className="stl-row" data-testid="stl-seller-active">
                      <Clock size={13} className="stl-icon" />
                      <span>{sellerLastActive}</span>
                    </div>
                  )}

                  {/* 1c. Seller response hint — derived from
                         ad.seller.response_score which is embedded in
                         the ad-details payload by the backend. NO extra
                         network call. Same .stl-row wrapper so RTL /
                         spacing / alignment are inherited unchanged. */}
                  {sellerResponseHint && (
                    <div className="stl-row" data-testid="stl-seller-response">
                      <Clock size={13} className="stl-icon" />
                      <span>{sellerResponseHint}</span>
                    </div>
                  )}

                  {/* 2. Verification badges */}
                  <div className="stl-badges">
                    {isVerified && (
                      <span className="stl-badge stl-badge-verified" data-testid="stl-verified-badge">
                        <ShieldCheck size={12} /> {isArabic ? 'موثق' : 'Verified'}
                      </span>
                    )}
                    {hasPhone && (
                      <span className="stl-badge stl-badge-ok" data-testid="stl-phone-badge">
                        <Check size={11} /> {t('adDetails.phoneVerified')}
                      </span>
                    )}
                    {hasEmail && (
                      <span className="stl-badge stl-badge-ok" data-testid="stl-email-badge">
                        <Check size={11} /> {isArabic ? 'بريد إلكتروني موثق' : 'Email verified'}
                      </span>
                    )}
                  </div>

                  {/* 3. Active listings */}
                  {activeCount > 0 && (
                    <div className="stl-row" data-testid="stl-active-count">
                      <Package size={13} className="stl-icon" />
                      <span>{activeCount} {t('adDetails.activeListings')}</span>
                    </div>
                  )}

                  {/* 4. Total listings (fallback to active count) */}
                  {(activeCount > 0 || (seller.stats?.total_ads_count ?? 0) > 0) && (
                    <div className="stl-row stl-row-muted" data-testid="stl-total-count">
                      <BarChart3 size={13} className="stl-icon" />
                      <span>{seller.stats?.total_ads_count || activeCount} {isArabic ? 'إجمالي الإعلانات' : 'total listings'}</span>
                    </div>
                  )}

                  {/* Profile completeness */}
                  {sellerCompleteness > 0 && (
                    <div className="stl-completeness">
                      <div className="stl-completeness-head">
                        <span>{t('adDetails.profileCompleteness')}</span>
                        <span className="stl-completeness-pct">{sellerCompleteness}%</span>
                      </div>
                      <div className="stl-completeness-bar">
                        <div className="stl-completeness-fill" style={{ width: `${sellerCompleteness}%` }} />
                      </div>
                    </div>
                  )}

                  {/* iter317/iter349 — "Rate seller" CTA. Visible to
                      everyone except the owner. The button's label
                      + tap behavior now reflect the prefetched
                      eligibility so the modal is only opened for
                      eligible buyers — ineligible buyers get an
                      inline toast with the same wording that the
                      sheet uses. This kills the "form flashes then
                      warning" bug at the origin. */}
                  {!isOwner && (ad?.user_id || ad?.seller?.user_id) && (() => {
                    const canReview = !reviewEligibility || reviewEligibility.eligible;
                    const label = canReview
                      ? t('reviews.rateSellerCta')
                      : (isArabic ? 'تواصل مع البائع أولاً' : 'Contact seller first to review');
                    const handleClick = () => {
                      if (!user) {
                        toast.info(t('reviews.gate.notAuthenticated'));
                        navigate('/auth', { state: { from: { pathname: location.pathname } } });
                        return;
                      }
                      if (reviewEligibility && !reviewEligibility.eligible) {
                        // Not eligible — surface the same message the
                        // sheet renders inline. NO modal opened, so no
                        // flash and no accidental dismissal.
                        toast.info(t('reviews.gate.needsInteraction'));
                        return;
                      }
                      setShowLeaveReviewModal(true);
                    };
                    return (
                      <button
                        type="button"
                        className={`rate-seller-cta${canReview ? '' : ' rate-seller-cta--gated'}`}
                        onClick={handleClick}
                        data-testid="rate-seller-cta"
                        data-eligible={canReview ? 'true' : 'false'}
                      >
                        <Star size={16} />
                        <span>{label}</span>
                      </button>
                    );
                  })()}
                </div>
              );
            })()}

            <div className="ad-actions-buttons">
              {isOwner && !ad.is_boosted && !COINS_DISABLED && (
                <button
                  onClick={() => setShowBoostModal(true)}
                  className="btn btn-boost"
                  data-testid="boost-ad-btn"
                >
                  <Zap size={18} />
                  <span>{t('adDetails.boostAd')}</span>
                </button>
              )}
            </div>

            <BoostTimerBadge
              isBoosted={ad.is_boosted}
              boostedUntil={ad.boosted_until}
              status={ad.status}
              visibility={ad.visibility}
              availability={ad.availability_status}
              lang={lang}
            />

            <div className="ad-share-section">
              {/* iter312 — Section title upgraded to "Promote this ad"
                  per operator request. The underlying AdShare component
                  was extended in-place (no duplicate share component). */}
              <h3 className="section-subtitle">{t('adDetails.promoteThisAd')}</h3>
              <AdShare
                adId={adId}
                title={titleText}
                price={formatMoney(ad.price, { locale: isArabic ? 'ar-SD' : 'en', currency: ad.currency || 'SDG' })}
                city={locationText}
                imageUrl={images?.[0] ? getImageUrl(images[0]) : null}
              />
            </div>

            {/* More from this seller */}
            {sellerAds.length >= 1 && (
              <div className="ad-more-from-seller" data-testid="more-from-seller">
                <div className="mfs-header">
                  <h3 className="section-subtitle">{t('adDetails.moreFromSeller')}</h3>
                  {(ad?.user_id || ad?.seller?.user_id) && (
                    <button
                      className="mfs-see-all"
                      onClick={() => navigate(`/seller/${ad?.user_id || ad?.seller?.user_id}`)}
                      data-testid="mfs-see-all-btn"
                    >
                      {t('adDetails.seeAllListings')}
                    </button>
                  )}
                </div>
                <div className="mfs-grid">
                  {sellerAds.map(sAd => (
                    <button
                      key={sAd.ad_id}
                      className="mfs-card"
                      onClick={() => navigate(`/ad/${sAd.ad_id}`)}
                      data-testid={`mfs-ad-${sAd.ad_id}`}
                    >
                      <div className="mfs-card-img">
                        <img
                          src={guardImage(getImageUrl(sAd.images?.[0]), sAd.category || sAd.category_id)}
                          alt=""
                          loading="lazy"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>
                      <div className="mfs-card-body">
                        <div className="mfs-card-title">{sAd.title}</div>
                        <div className="mfs-card-price">
                          {sAd.price
                            ? formatMoney({ amount: sAd.price, currency: sAd.currency || 'SDG', lang: isArabic ? 'ar' : 'en' })
                            : t('adDetails.priceOnRequest')}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isSuspectedUnavailable && (
              <div className="suspected-unavailable-banner" data-testid="suspected-unavailable-banner">
                <div className="suspected-unavailable-icon">
                  <AlertCircle size={18} />
                </div>
                <div className="suspected-unavailable-content">
                  <span className="suspected-unavailable-text">
                    {t('adDetails.mayBeUnavailable')}
                  </span>
                </div>
              </div>
            )}

            {ad && (
              <RelatedAdsCarousel
                adId={adId}
                categoryId={ad.category}
                categorySlug={ad.category}
                city={locationText || ad.city || ad.state || ad.location}
                lang={lang}
                currentAd={ad}
              />
            )}
          </div>
        </div>
      </div>

      <div className="ad-sticky-contact-bar" data-testid="ad-sticky-contact-bar">
        {isUnavailable ? (
          <button
            className="sticky-contact-btn sticky-contact-full"
            onClick={() => navigate(`/shop?category=${ad.category_id || ad.category}`)}
            data-testid="sticky-browse-similar-btn"
          >
            <Search size={18} />
            <span>{t('adDetails.browseSimilar')}</span>
          </button>
        ) : (
          <div className="sticky-contact-grid sticky-contact-3col">
            <button
              className="sticky-contact-btn sticky-contact-whatsapp sticky-contact-primary"
              onClick={() => handleStickyAction('whatsapp')}
              data-testid="sticky-whatsapp-btn"
              disabled={!hasPrimaryWhatsAppCTA}
            >
              <WhatsAppIcon size={18} />
              <span>WhatsApp</span>
            </button>
            <button
              className="sticky-contact-btn sticky-contact-message"
              onClick={() => handleStickyAction('message')}
              data-testid="sticky-message-btn"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
              <span>{t('adDetails.message')}</span>
            </button>
            <button
              className={`sticky-contact-btn sticky-contact-secondary ${isFavorite(adId) ? 'saved-state' : ''}`}
              onClick={() => handleStickyAction('save')}
              data-testid="sticky-save-btn"
            >
              <Heart 
                size={18} 
                fill={isFavorite(adId) ? 'currentColor' : 'none'} 
                className={isFavorite(adId) ? 'save-heart-animate' : ''}
              />
              <span>
                {isFavorite(adId)
                  ? t('adDetails.saved')
                  : t('adDetails.save')}
              </span>
            </button>
          </div>
        )}
      </div>
      
      {/* Boost Selection Modal - Only for owners */}
      {isOwner && (
        <BoostSelectionModal
          open={showBoostModal}
          onClose={() => setShowBoostModal(false)}
          ad={ad}
          userCoins={user?.coins || 0}
          onBoostSuccess={(data) => {
            // iter342 — Sync canonical coin balance across the app so
            // header, wallet, account, and boost modal all reflect
            // the deduction from a single source of truth.
            if (typeof data?.new_balance === 'number') {
              try { updateUser({ coins: data.new_balance }); } catch (_) { /* no-op */ }
            }
            // Update ad state to show boosted badge
            setAd(prev => ({
              ...prev,
              is_boosted: true,
              boost_until: data.expires_at || data.boost_until,
              // Update specific boost flags based on boost_code
              is_highlighted: data.boost_code === 'BOOST_HIGHLIGHT_24H' || prev.is_highlighted,
              has_urgent_badge: data.boost_code === 'BADGE_URGENT_7D' || prev.has_urgent_badge,
              is_top_category: data.boost_code === 'BOOST_TOP_CATEGORY_24H' || prev.is_top_category,
            }));
            setShowBoostModal(false);
          }}
        />
      )}
      
      {/* Unified Report Ad Sheet (Bottom Sheet) */}
      <ReportAdSheet
        open={showReportSheet}
        onClose={() => setShowReportSheet(false)}
        adId={adId}
        onReported={(data) => {
          setHasReported(true);
          // If report triggers suspected_unavailable, update ad
          if (data?.status === 'suspected_unavailable') {
            setAd(prev => ({
              ...prev,
              availability_status: 'suspected_unavailable',
              report_count: data.report_count
            }));
          }
        }}
      />

      {/* iter317 — Seller reviews sheet + Leave review modal.
          The sheet shows the public list; the modal is the write form,
          gated server-side by db.contact_logs. */}
      {(ad?.user_id || ad?.seller?.user_id) && (
        <>
          <SellerReviewsSheet
            sellerUserId={ad?.user_id || ad?.seller?.user_id}
            open={showReviewsSheet}
            onClose={() => setShowReviewsSheet(false)}
            onLeaveReview={!isOwner ? () => {
              // iter348 — Do NOT unmount the sheet while opening the
              // Leave Review modal. Unmounting mid-tap caused the
              // same pointerup to land on the freshly-mounted modal
              // backdrop and dismiss it. LeaveReviewModal has a
              // higher z-index and portals to <body>, so stacking it
              // over the sheet is safe. The sheet closes when the
              // user closes it explicitly.
              setShowLeaveReviewModal(true);
            } : null}
          />
          <LeaveReviewModal
            sellerUserId={ad?.user_id || ad?.seller?.user_id}
            adId={adId}
            open={showLeaveReviewModal}
            onClose={() => setShowLeaveReviewModal(false)}
            onSubmitted={() => {
              setShowLeaveReviewModal(false);
              refreshReviewEligibility?.();
              toast.success(isArabic ? 'تم إرسال تقييمك. شكراً!' : 'Thanks — your review was submitted.');
            }}
          />
        </>
      )}
      
      {/* Posted Details Bottom Sheet Modal */}
      {showPostedDetails && (
        <div
          className="posted-details-overlay"
          onClick={() => setShowPostedDetails(false)}
          data-testid="posted-details-modal"
        >
          <div
            className="posted-details-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="posted-details-header">
              <div className="posted-details-title">
                {t('adDetails.postDetails')}
              </div>
              <button
                type="button"
                className="posted-details-close"
                onClick={() => setShowPostedDetails(false)}
                aria-label={t('close')}
              >
                ×
              </button>
            </div>

            <div className="posted-details-content">
              <div className="posted-details-row">
                <span className="posted-details-label">
                  {t('adDetails.postedAt')}
                </span>
                <span className="posted-details-value">{exactPosted || '-'}</span>
              </div>

              {ad?.updated_at && (
                <div className="posted-details-row">
                  <span className="posted-details-label">
                    {t('adDetails.lastUpdated')}
                  </span>
                  <span className="posted-details-value">{exactUpdated || '-'}</span>
                </div>
              )}
            </div>

            <button
              type="button"
              className="posted-details-done"
              onClick={() => setShowPostedDetails(false)}
            >
              {t('adDetails.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdDetails;
